import { ChevronsUpDown, LogOut, Shield, User as UserIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSidebar } from '@/components/ui/sidebar-context';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar-menu';
import { clearAccessToken, signOutRequest, useCurrentUser } from '@/features/auth';

function getInitials(name: string | null, email: string): string {
  if (name) {
    const initials = name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
    if (initials.length > 0) return initials;
  }
  return email[0]?.toUpperCase() ?? '?';
}

interface IdentityBlockProps {
  readonly name: string;
  readonly email: string;
  readonly avatarUrl: string | null;
  readonly initials: string;
  readonly hideText?: boolean;
}

function IdentityBlock({ name, email, avatarUrl, initials, hideText = false }: IdentityBlockProps) {
  return (
    <>
      <Avatar
        key={avatarUrl ?? 'no-image'}
        className="aspect-square size-8 shrink-0 rounded-lg group-data-[collapsible=icon]:size-5"
      >
        {avatarUrl ? <AvatarImage src={avatarUrl} alt="" className="object-cover" /> : null}
        <AvatarFallback className="rounded-lg text-sm group-data-[collapsible=icon]:text-[9px]">
          {initials}
        </AvatarFallback>
      </Avatar>
      {hideText ? null : (
        <div className="grid flex-1 text-left text-sm leading-tight">
          <span className="truncate font-medium">{name}</span>
          <span className="truncate text-xs text-muted-foreground">{email}</span>
        </div>
      )}
    </>
  );
}

interface UserMenuItemsProps {
  readonly identity: React.ReactNode;
  readonly isAdmin: boolean;
  readonly onSignOut: () => void;
  readonly onNavigate: () => void;
}

function UserMenuItems({ identity, isAdmin, onSignOut, onNavigate }: UserMenuItemsProps) {
  return (
    <>
      <DropdownMenuLabel className="p-0 font-normal">
        <div className="flex items-center gap-2 px-2 py-2 text-left text-sm">{identity}</div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem asChild className="min-h-11 cursor-pointer">
          <Link to="/profile" onClick={onNavigate}>
            <UserIcon />
            <span>Profile</span>
          </Link>
        </DropdownMenuItem>
        {isAdmin ? (
          <DropdownMenuItem asChild className="min-h-11 cursor-pointer">
            <Link to="/admin/users" onClick={onNavigate}>
              <Shield />
              <span>User management</span>
            </Link>
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onSelect={onSignOut}
        className="min-h-11 cursor-pointer"
        aria-label="Log out"
      >
        <LogOut />
        <span>Log out</span>
      </DropdownMenuItem>
    </>
  );
}

function useSignOut(): () => void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return () => {
    // Revoke the refresh token server-side (best-effort) so the session can't
    // be resumed from the cookie, then tear down local state and redirect.
    void signOutRequest();
    clearAccessToken();
    queryClient.clear();
    void navigate('/sign-in', { replace: true });
  };
}

interface ViewModel {
  readonly triggerLabel: string;
  readonly identityProps: IdentityBlockProps;
}

function toViewModel(user: {
  fullName: string | null;
  email: string;
  avatarUrl: string | null;
}): ViewModel {
  const trimmedName = user.fullName?.trim() ?? '';
  const displayName = trimmedName.length > 0 ? trimmedName : user.email;
  const triggerLabel = trimmedName.length > 0 ? `${trimmedName} (${user.email})` : user.email;
  return {
    triggerLabel,
    identityProps: {
      name: displayName,
      email: user.email,
      avatarUrl: user.avatarUrl,
      initials: getInitials(user.fullName, user.email),
    },
  };
}

export function UserMenu() {
  const { user, isLoading, isAdmin } = useCurrentUser();
  const { isMobile, state, setOpenMobile } = useSidebar();
  const handleSignOut = useSignOut();
  if (isLoading || !user) return null;

  const collapsed = state === 'collapsed';
  const { triggerLabel, identityProps } = toViewModel(user);
  const handleNavigate = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              tooltip={triggerLabel}
              aria-label={`Open account menu for ${triggerLabel}`}
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <IdentityBlock {...identityProps} hideText={collapsed} />
              {collapsed ? null : <ChevronsUpDown className="ml-auto size-4" />}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side={isMobile ? 'top' : 'right'}
            align="end"
            sideOffset={4}
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
          >
            <UserMenuItems
              identity={<IdentityBlock {...identityProps} />}
              isAdmin={isAdmin}
              onSignOut={handleSignOut}
              onNavigate={handleNavigate}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
